import useNotification from 'hooks/useNotification';
import {
  BgColorsOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  EditFilled,
  ProfileOutlined,
  SettingOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { COURSE_ICONS, COURSE_ICONS_PATH } from 'constants/course-icons';
import {
  Divider,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Switch,
  Transfer,
  Typography
} from 'antd';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { ICertificateKey, ICourse } from 'interfaces/courses';
import { IInstructor } from 'interfaces/instructors';
import { IProductCategory } from 'interfaces/product_categories';
import { Key, useCallback, useState } from 'react';
import { getTenant } from 'services/tenants';
import { useList } from '@refinedev/core';
import { useModalForm, useSelect } from '@refinedev/antd';

interface CreateCourseModalProps {
  open: boolean;
  onClose: (close: boolean) => void;
}

export const CreateCourseModal = ({
  open,
  onClose
}: CreateCourseModalProps) => {
  const { showError, showSuccess } = useNotification();
  const tenant = getTenant();
  const ssoQcDisabled = tenant.disabledFeatures?.includes('sso_qc');
  const [courseType, setCourseType] = useState<'regular' | 'on_demand'>(
    'regular'
  );

  const { modalProps, formProps } = useModalForm<ICourse>({
    action: 'create',
    redirect: 'edit',
    resource: 'courses',
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: () => {
      showSuccess('Curso criado com sucesso');
      onClose(false);
      formProps.form?.resetFields();
    },
    onMutationError: () => {
      showError('Erro ao criar curso. Tente novamente.');
    }
  });

  const { selectProps: productCategorySelectProps } =
    useSelect<IProductCategory>({
      resource: 'products/categories',
      optionLabel: 'name',
      optionValue: 'name',

      onSearch: value => [
        {
          field: 'filter[by_name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server'
      }
    });

  const [searchText, setSearchText] = useState('');

  const { selectProps: certificateSelectProps } = useSelect<ICertificateKey>({
    resource: 'certificate_keys',
    optionLabel: 'key',
    optionValue: 'key',
    pagination: { mode: 'server' }
  });

  const { data: instructorsData } = useList<IInstructor>({
    resource: 'instructors',
    filters: searchText
      ? [
          {
            field: 'filter[name]',
            operator: 'eq',
            value: searchText
          }
        ]
      : [],
    pagination: {
      mode: 'server',
      pageSize: 9999
    }
  });

  const instructors =
    instructorsData?.data.map(instructor => ({
      key: instructor.id,
      title: instructor.name
    })) ?? [];

  const [selectedInstructorIds, setSelectedInstructorIds] = useState<Key[]>([]);

  const onCloseModal = useCallback(() => {
    formProps.form?.resetFields();
    setCourseType('regular');
    onClose(false);
  }, [formProps.form, onClose]);

  return (
    <Modal
      {...modalProps}
      open={open}
      onCancel={onCloseModal}
      title="Novo curso avulso"
      okText="Criar curso"
      cancelText="Cancelar"
      width={800}
    >
      <Form {...formProps} layout="vertical">
        {/* Informações Gerais */}
        <fieldset>
          <FieldsetTitle title="Informações Gerais" icon={ProfileOutlined} />

          <div className="pl-6">
            <FormItemCol
              style={{ marginBottom: 16 }}
              cols={[6, 18]}
              label="Nome"
              name="name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Ex.: Direito Civil" />
            </FormItemCol>

            <FormItemCol
              style={{ marginBottom: 0 }}
              cols={[6, 18]}
              label="Versão"
              name="kind"
              rules={[{ required: true }]}
              initialValue={
                tenant.disabledFeatures?.includes('courses_v2') ? 'v1' : 'v2'
              }
            >
              <Radio.Group>
                {!tenant.disabledFeatures?.includes('courses_v2') && (
                  <Radio.Button value="v2">V2 (com turmas)</Radio.Button>
                )}
                <Radio.Button value="v1">V1 (depreciada)</Radio.Button>
              </Radio.Group>
            </FormItemCol>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Dados do curso */}
        <fieldset>
          <FieldsetTitle title="Dados do curso" icon={EditFilled} />
          <div className="pl-6">
            <FormItemCol
              cols={[6, 18]}
              style={{ marginBottom: 16 }}
              label="Duração (horas)"
              name="duration_in_hours"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} />
            </FormItemCol>

            <FormItemCol
              cols={[6, 18]}
              style={{ marginBottom: 0 }}
              label="Nota mínima (0-100)"
              name="min_evaluation_score"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={100} />
            </FormItemCol>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Configuração de curso */}
        <fieldset>
          <FieldsetTitle title="Configuração de curso" icon={SettingOutlined} />
          <div className="pl-6">
            <FormItemCol
              cols={[6, 18]}
              style={{ marginBottom: 16 }}
              label="Tipo de curso"
              name="course_type"
              initialValue="regular"
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={e => setCourseType(e.target.value)}
                value={courseType}
              >
                <Radio.Button value="regular">Regular</Radio.Button>
                <Radio.Button value="on_demand">Flexível</Radio.Button>
              </Radio.Group>
            </FormItemCol>

            {courseType === 'on_demand' && (
              <FormItemCol
                cols={[6, 18]}
                style={{ marginBottom: 16 }}
                label="Dias mínimos para aprovação"
                name="minimum_days_to_approve"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório para cursos flexíveis'
                  }
                ]}
              >
                <InputNumber min={0} />
              </FormItemCol>
            )}

            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 0 }}
            >
              <strong>Regular:</strong> Curso com datas de início e término
              definidas. O certificado é liberado somente após o fechamento de
              todas as turmas.
              <br />
              <strong>Flexível:</strong> O aluno pode iniciar e concluir o curso
              no seu próprio ritmo. O certificado é liberado após a conclusão do
              curso, respeitando o prazo mínimo configurado.
            </Typography.Text>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Instrutores */}
        <fieldset>
          <FieldsetTitle
            title="Instrutores"
            icon={TeamOutlined}
            hint="Os instrutores aparecem na vitrine do curso"
          />
          <div className="pl-6">
            <Form.Item
              label={false}
              name="instructor_ids"
              rules={[{ required: true }]}
            >
              <Transfer
                titles={['Instrutores', 'Selecionados']}
                dataSource={instructors}
                targetKeys={selectedInstructorIds}
                onSearch={(_, value) => setSearchText(value)}
                onChange={setSelectedInstructorIds}
                render={item => item.title}
                filterOption={() => true}
                listStyle={{ width: '100%', height: 250 }}
                oneWay
                showSearch
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
        </fieldset>

        <Divider style={{ marginBottom: 16, marginTop: 0 }} />

        {/* Certificado */}
        <fieldset>
          <FieldsetTitle
            title="Certificado"
            icon={CheckCircleOutlined}
            hint="Escolha o modelo de certificado que o aluno irá receber. Você não pode alterar essa informação posteriormente"
          />
          <div className="pl-6">
            <Form.Item
              name="certificate_template_key"
              rules={[{ required: true }]}
            >
              <Select
                {...certificateSelectProps}
                placeholder="Escolha o certificado"
              />
            </Form.Item>
            <Form.Item
              label="Tipo de emissão de certificado"
              name="certificate_automatic"
              initialValue={true}
              style={{ marginBottom: 0 }}
              extra={
                <p style={{ marginTop: 8 }}>
                  <strong>Automática:</strong> O aluno pode emitir o certificado
                  diretamente pela plataforma.
                  <br />
                  <strong>Manual:</strong> A emissão do certificado é feita pela
                  secretaria.
                </p>
              }
            >
              <Radio.Group>
                <Radio.Button value={true}>Automática</Radio.Button>
                <Radio.Button value={false}>Manual</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* VadeMecum */}
        <fieldset>
          <FieldsetTitle
            title="Funcionalidades do curso"
            icon={BuildOutlined}
            hint="Ative funcionalidades que estarão disponíveis para alunos deste curso"
          />
          <div className="pl-6">
            <Form.Item
              name="has_vade_mecum"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 0 }}
            >
              <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                <div>
                  <div className="font-semibold text-base mb-1">
                    Vade Mecum Digital
                  </div>
                  <div className="text-gray-500 text-sm">
                    Biblioteca digital completa pra consulta da constituição,
                    leis e códigos
                  </div>
                </div>
                <Switch
                  onChange={checked =>
                    formProps.form?.setFieldValue('has_vade_mecum', checked)
                  }
                />
              </div>
            </Form.Item>
            {!ssoQcDisabled && (
              <Form.Item
                name={['media', 'sso_qc']}
                valuePropName="checked"
                initialValue={false}
                style={{ marginBottom: 0, marginTop: 16 }}
              >
                <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                  <div>
                    <div className="font-semibold text-base mb-1">
                      Acesso a questões no Qconcursos
                    </div>
                    <div className="text-gray-500 text-sm">
                      Acesso automático a mesa de estudos e o banco de questões
                      do Qconcursos, sem precisar de novo login (SSO).
                    </div>
                  </div>
                  <Switch
                    onChange={checked =>
                      formProps.form?.setFieldValue(
                        ['media', 'sso_qc'],
                        checked
                      )
                    }
                  />
                </div>
              </Form.Item>
            )}
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Personalizacao */}
        <fieldset>
          <FieldsetTitle
            title="Personalização e categorização"
            icon={BgColorsOutlined}
            hint="Adicione um ícone e etiqueta para representar e classificar seu curso. Essas informações ajudam na organização e poderão ser alteradas depois."
          />
          <div className="pl-6 [&>div]:!mb-4">
            <Form.Item
              name={['media', 'tags']}
              label="Etiquetas"
              extra={
                <Typography.Text type="secondary" className="block mt-2">
                  Para editar as etiquetas entre em Loja Online `{'>'}`
                  Categorias
                </Typography.Text>
              }
            >
              <Select
                {...productCategorySelectProps}
                placeholder="Selecione uma ou mais etiquetas..."
                mode="multiple"
              />
            </Form.Item>

            <Form.Item
              name={['media', 'icon']}
              label="Ícone"
              initialValue={COURSE_ICONS[0]}
            >
              <Typography.Text
                type="secondary"
                className="block text-secondary-500 mt-0 mb-4"
              >
                Escolha o ícone que irá aparecer no card da tela de cursos do
                aluno
              </Typography.Text>

              <Radio.Group
                rootClassName="course-icons-radio-wrapper"
                onChange={e => {
                  formProps?.form?.setFieldValue(
                    ['media', 'icon'],
                    e.target.value
                  );
                }}
                defaultValue={COURSE_ICONS[0]}
                options={COURSE_ICONS.map((icon, index) => ({
                  value: icon,
                  label: (
                    <Flex vertical gap={4}>
                      <Image
                        preview={false}
                        src={`${COURSE_ICONS_PATH}/${icon}`}
                        alt={icon}
                        width={50}
                        height={50}
                      />
                      {index === 0 && (
                        <Typography.Text type="secondary">
                          Padrão
                        </Typography.Text>
                      )}
                    </Flex>
                  )
                }))}
              />
            </Form.Item>
          </div>
        </fieldset>
      </Form>
    </Modal>
  );
};
